import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { Shirt } from "lucide-react";

/**
 * Grille tarifaire GLOBALE Blanchisserie & Ameublement.
 * Éditable par l'admin (prix TTC uniquement).
 */
export default function AdminLaundryPricing() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // { article_id: new_price }
  const [saving, setSaving] = useState({});

  const load = async () => {
    try {
      const res = await api.get("/admin/laundry-pricing");
      setArticles(res.data || []);
    } catch {
      toast({ title: "Erreur", description: "Impossible de charger la grille tarifaire.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveArticle = async (article_id) => {
    const raw = edits[article_id];
    if (raw === undefined) return;
    const price = Number(raw);
    if (Number.isNaN(price) || price < 0) {
      toast({ title: "Prix invalide", description: "Saisissez un nombre positif.", variant: "destructive" });
      return;
    }
    setSaving((s) => ({ ...s, [article_id]: true }));
    try {
      await api.put(`/admin/laundry-pricing/${article_id}`, { price_ttc: price });
      setArticles((prev) => prev.map((a) => (a.id === article_id ? { ...a, price_ttc: price } : a)));
      setEdits((e) => { const nx = { ...e }; delete nx[article_id]; return nx; });
      toast({ title: "Prix mis à jour" });
    } catch {
      toast({ title: "Erreur", description: "Sauvegarde impossible.", variant: "destructive" });
    } finally {
      setSaving((s) => ({ ...s, [article_id]: false }));
    }
  };

  if (loading) return <div className="text-slate-400 text-sm">Chargement…</div>;

  const grouped = articles.reduce((acc, a) => {
    (acc[a.category] ||= []).push(a);
    return acc;
  }, {});

  const catLabel = (c) => c === "blanchisserie" ? "Blanchisserie" : c === "ameublement" ? "Ameublement" : c;

  return (
    <div data-testid="admin-laundry-pricing" className="space-y-6">
      <div className="text-sm text-slate-300 flex items-center gap-2">
        <Shirt className="h-4 w-4" />
        Grille tarifaire globale — utilisée pour les demandes propriétaires (Dégraissage/Blanchisserie).
      </div>
      {Object.keys(grouped).sort().map((cat) => (
        <div key={cat} data-testid={`admin-laundry-pricing-category-${cat}`} className="rounded-lg border border-white/10 bg-white/[0.03] p-4">
          <div className="mb-3 text-sm font-medium text-slate-100">{catLabel(cat)}</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {grouped[cat].map((a) => {
              const isEditing = edits[a.id] !== undefined;
              const current = isEditing ? edits[a.id] : a.price_ttc;
              return (
                <div key={a.id} data-testid={`admin-laundry-article-${a.id}`} className="flex items-center justify-between gap-2 rounded-md border border-white/5 bg-white/[0.02] px-3 py-2">
                  <div className="text-sm text-slate-200 flex-1 truncate">{a.name}</div>
                  <div className="flex items-center gap-1.5">
                    <Input
                      data-testid={`admin-laundry-article-${a.id}-price`}
                      type="number"
                      step="0.10"
                      min="0"
                      value={current}
                      onChange={(e) => setEdits((prev) => ({ ...prev, [a.id]: e.target.value }))}
                      className="h-8 w-24 border-white/10 bg-white/[0.06] text-sm"
                    />
                    <span className="text-xs text-slate-400">€</span>
                    {isEditing && (
                      <Button
                        data-testid={`admin-laundry-article-${a.id}-save`}
                        size="sm"
                        className="h-8 rounded-full bg-violet-500 text-white hover:bg-violet-400 px-3"
                        disabled={!!saving[a.id]}
                        onClick={() => saveArticle(a.id)}
                      >
                        {saving[a.id] ? "…" : "OK"}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
